import {
	AfterViewInit,
	Component,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS_PCDS,
	InstanciaRelatorio,
} from '../../../interfaces/tipos-relatorios';
import { Validators } from '@angular/forms';
import { Estacao } from '../../../interfaces/filtros-opcoes';
import { Select } from '@layout/interfaces/select';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import {
	alinharDado,
	baixarEAbrirPDF,
	capitalizeFirstLetter,
	criarImagemBase64FromChart,
	enumAsSelectOptions,
	isNotNuloOuUndefined,
	numberToBrNumberTables,
	obter_erro_request,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import { ToastrService } from 'ngx-toastr';
import {
	ConfiguracaoGrafico,
	Variavel,
	VariavelUnidade,
} from '../../../interfaces/VariaveisMeteorologicas';
import {
	mapearDados,
	obterDadosSeriePorResultado,
	VARIAVEIS_DIRECAO_VENTO,
} from './obter-serie-resultado';
import {
	rangeSelector5Dias,
	rangeSelector5Horas,
	rangeSelector5Meses,
} from '@componentes/charts/range-selector';
import { DateTimeUtils } from '@utils/datetime-util';
import * as pdfseira from '@utils/pdf-seira';
import { RelatoriosService } from '../../../services';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { format } from 'date-fns';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { HighchartsNoData } from '@componentes/high-stock/interfaces/highcharts';
import { ResponsiveOptions, SeriesOptionsType } from 'highcharts';
import { configResponsividade } from '@componentes/charts/regras-responsividade';
import { HighStockComponent } from '@componentes/high-stock/high-stock.component';
import deepmerge from 'deepmerge';
import * as Highcharts from 'highcharts/highstock';
import ptBrLocale from 'date-fns/locale/pt-BR';
import { corrigeDuplicacaoNome } from '../../../utils';
import {
	RelatorioVariavelEstacao,
	RelatorioVariavelEstacaoVento,
	RelatorioDirecaoVentoEstacao,
} from '../../../submodulos/monitoramento/pages/variaveis/interfaces/estacao-ultimas-medicoes';
import { VariaveisService } from '../../../submodulos/monitoramento/services/variaveis.service';
import { VariavelAcompanhamentoColeta } from '../../../submodulos/monitoramento/pages/variaveis/interfaces/variaveis';
import { filter, Subscription } from 'rxjs';
import moment from 'moment';
import { TipoPCD } from '@modulos/meteorologia/submodulos/estacao/enums/tipo-estacao';
export const PERIODO_MAXIMO_EM_DIAS_GRAFICO_PCD = 3;
export const PERIODO_MAXIMO_EM_MESES_GRAFICO_PCD = 12;
export const PERIODO_MAXIMO_EM_ANOS_GRAFICO_PCD = 2;
const TOLERANCIA_DUPLICIDADE_TICK_EIXO_X = 1;
const TOLERANCIA_DUPLICIDADE_TICK_EIXO_Y = 0.01;
const PROPORCAO_MINIMA_ENTRE_TICKS_EIXO_Y = 0.15;
const opcoesLabelInicioEixoX = {
	allowOverlap: true,
	crop: false,
	overflow: 'allow' as const,
	y: 20,
	step: 1,
};

function obterTicksComInicioEixoX(
	ticksAutomaticos: number[],
	min?: number | null,
	max?: number | null
): number[] {
	if (!Number.isFinite(min) || !Number.isFinite(max)) {
		return ticksAutomaticos;
	}

	const eixoMin = min as number;
	const eixoMax = max as number;
	return [eixoMin, ...ticksAutomaticos]
		.filter(tick => Number.isFinite(tick))
		.filter(tick => {
			return (
				tick >= eixoMin - TOLERANCIA_DUPLICIDADE_TICK_EIXO_X &&
				tick <= eixoMax + TOLERANCIA_DUPLICIDADE_TICK_EIXO_X
			);
		})
		.map(tick => {
			return Math.abs(tick - eixoMin) <= TOLERANCIA_DUPLICIDADE_TICK_EIXO_X
				? eixoMin
				: tick;
		})
		.sort((a, b) => a - b)
		.filter((tick, index, ticks) => {
			return (
				index === 0 ||
				Math.abs(tick - ticks[index - 1]) > TOLERANCIA_DUPLICIDADE_TICK_EIXO_X
			);
		});
}

function deveAplicarTickInicialEixoX(periodo: string): boolean {
	return ['diario', 'mensal', 'anual'].includes(periodo);
}

function formatarLabelEixoY(valor: string | number): string {
	const numero = Number(valor);
	if (!Number.isFinite(numero)) {
		return String(valor);
	}
	const numeroArredondado = Number(numero.toFixed(1));
	const valorFormatado = Number.isInteger(numeroArredondado)
		? numeroArredondado.toString()
		: numeroArredondado.toFixed(1);
	return valorFormatado.replace('.', ',');
}

function obterTicksComExtremosDadosYAxis(
	ticksAutomaticos: number[],
	dataMin?: number | null,
	dataMax?: number | null
): number[] {
	if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
		return ticksAutomaticos;
	}

	const menorValor = dataMin as number;
	const maiorValor = dataMax as number;
	const distanciaMinimaEntreTicks =
		(maiorValor - menorValor) * PROPORCAO_MINIMA_ENTRE_TICKS_EIXO_Y;
	return [...ticksAutomaticos, dataMin, dataMax]
		.filter((tick): tick is number => Number.isFinite(tick))
		.filter(tick => {
			return (
				tick >= menorValor - TOLERANCIA_DUPLICIDADE_TICK_EIXO_Y &&
				tick <= maiorValor + TOLERANCIA_DUPLICIDADE_TICK_EIXO_Y
			);
		})
		.map(tick => {
			if (Math.abs(tick - menorValor) <= TOLERANCIA_DUPLICIDADE_TICK_EIXO_Y) {
				return menorValor;
			}
			if (Math.abs(tick - maiorValor) <= TOLERANCIA_DUPLICIDADE_TICK_EIXO_Y) {
				return maiorValor;
			}
			return tick;
		})
		.filter(tick => {
			return (
				tick === menorValor ||
				tick === maiorValor ||
				(Math.abs(tick - menorValor) >= distanciaMinimaEntreTicks &&
					Math.abs(tick - maiorValor) >= distanciaMinimaEntreTicks)
			);
		})
		.sort((a, b) => a - b)
		.filter((tick, index, ticks) => {
			return (
				index === 0 ||
				Math.abs(tick - ticks[index - 1]) > TOLERANCIA_DUPLICIDADE_TICK_EIXO_Y
			);
		});
}

const criarYAxisEsquerda = (
	configuracao: Highcharts.YAxisOptions = {}
): Highcharts.YAxisOptions => ({
	opposite: false,
	labels: {
		align: 'left',
		x: 3,
		allowOverlap: true,
		formatter: function () {
			return formatarLabelEixoY(this.value);
		},
	},
	showFirstLabel: true,
	showLastLabel: true,
	minPadding: 0.05,
	maxPadding: 0.08,
	tickPositioner: function (this: Highcharts.Axis) {
		const eixo = this as Highcharts.Axis & {
			dataMin?: number;
			dataMax?: number;
		};
		return obterTicksComExtremosDadosYAxis(
			eixo.tickPositions ?? [],
			eixo.dataMin,
			eixo.dataMax
		);
	},
	...configuracao,
});

const VARIAVEIS_GRAFICO_PCD_METEOROLOGICA = [
	'EVAPOTRANSPIRACAO',
	'PRECIPITACAO',
	'PRESSAO_ATMOSFERICA',
	'RADIACAO_SOLAR',
	'TEMPERATURA_DO_AR',
	'TEMPERATURA_MAXIMA',
	'TEMPERATURA_MINIMA',
	'UMIDADE_DO_AR',
	'UMIDADE_MAXIMA',
	'UMIDADE_MINIMA',
	'VENTO_DIRECAO_1',
	'VENTO_DIRECAO_1_NA_MAXIMA',
	'VENTO_VELOCIDADE_1',
	'VENTO_VELOCIDADE_1_MAXIMA',
];
const VARIAVEIS_GRAFICO_PCD_AGROMETEOROLOGICA = [
	...VARIAVEIS_GRAFICO_PCD_METEOROLOGICA,
	'CONTEUDO_AGUA_SOLO_10CM',
	'CONTEUDO_AGUA_SOLO_20CM',
	'CONTEUDO_AGUA_SOLO_30CM',
	'CONTEUDO_AGUA_SOLO_40CM',
	'CONTEUDO_AGUA_SOLO_50CM',
	'CONTEUDO_AGUA_SOLO_60CM',
	'TEMPERATURA_DO_SOLO_10CM',
	'TEMPERATURA_DO_SOLO_20CM',
	'TEMPERATURA_DO_SOLO_30CM',
	'TEMPERATURA_DO_SOLO_40CM',
	'TEMPERATURA_DO_SOLO_50CM',
	'TEMPERATURA_DO_SOLO_60CM',
	'VENTO_DIRECAO_2',
	'VENTO_DIRECAO_2_NA_MAXIMA',
	'VENTO_VELOCIDADE_2',
	'VENTO_VELOCIDADE_2_MAXIMA',
];

const TIPOS_PCD_GRAFICO: Select<string>[] = [
	{ value: 'METEOROLOGICA', name: 'Meteorológica' },
	{ value: 'AGROMETEOROLOGICA', name: 'Agrometeorológica' },
];

@Component({
	selector: 'seira-grafico-variaveis-meteorologicas',
	templateUrl: './grafico-variaveis-meteorologicas.component.html',
	styleUrls: ['./grafico-variaveis-meteorologicas.component.scss'],
})
export class GraficoVariaveisMeteorologicasComponent
	implements OnInit, OnDestroy, InstanciaRelatorio, AfterViewInit
{
	@ViewChild(HighStockComponent) highStockComponent!: HighStockComponent;
	descricaoRelatorio =
		'Define-se como variáveis meteorológicas os parâmetros atmosféricos, como temperatura, umidade, e velocidade do vento, que influenciam as condições climáticas de um local ou região.';
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => this.exportarPDF(),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	private inscricoes = new Subscription();
	chartOptions: Highcharts.Options = {
		credits: {
			enabled: false,
		},
		chart: {
			backgroundColor: 'transparent',
			renderTo: 'grafico',
			zooming: {
				mouseWheel: true,
			},
		},
		lang: {
			loading: undefined,
			noData: this.getNoDataMessage(),
		},
		noData: {
			useHTML: true,
		},
		rangeSelector: rangeSelector5Horas,
		legend: {
			enabled: false,
		},
		xAxis: {
			type: 'datetime',
			zoomEnabled: true,
			ordinal: false,
		},
		exporting: {
			enabled: true,
			allowHTML: true,
			buttons: {
				contextButton: {
					menuItems: [
						'viewFullscreen',
						'separator',
						'downloadPNG',
						'downloadJPEG',
					],
				},
			},
		},
		scrollbar: {
			enabled: true,
		},
		navigator: {
			enabled: false,
			// adaptToUpdatedData: false,
		},
		yAxis: [criarYAxisEsquerda()],
		plotOptions: {
			series: {
				getExtremesFromAll: false,
				softThreshold: false,
				dataGrouping: {
					enabled: false,
				},
			},
			bar: {
				dataLabels: {
					enabled: true,
				},
			},
		},
		responsive: configResponsividade as ResponsiveOptions,
	};
	inputs = inject(INPUTS_RELATORIOS_PCDS);
	estacoes: Estacao[] = [];
	dados!:
		| RelatorioVariavelEstacao[]
		| RelatorioVariavelEstacaoVento[]
		| RelatorioDirecaoVentoEstacao[];
	chart!: Highcharts.Chart & HighchartsNoData;
	medicoes: RelatorioVariavelEstacao[] = [];
	loading = false;
	variaveisUnidade: Select<string>[] = [];
	variaveisArray: Select<string>[] = [];
	variaveisGrafico: Select<string>[] = [];
	tiposPCD: Select<string>[] = [];
	opcoesMunPostos: Select<string>[] = [];
	variavelSelecionada = '';
	isVariavelVento = false;
	isVariavelDirecaoVento = false;
	tituloGrafico = '';
	MES_MILISEGUNDOS = 2592000000; // 30 dias
	SEMESTRE_MILISEGUNDOS = 15552000000; // 6 meses
	UM_DIA_MILISEGUNDOS = 86400000; // 1 dia
	mensagemDataMinima?: string;

	constructor(
		private relatoriosService: RelatoriosService,
		private variavelService: VariaveisService,
		private toastr: ToastrService
	) {
		this.estacoes = this.inputs.estacoes;
		this.ouvirMudancasMensagemDataMin();
		this.ouvirMudancasPeriodoBusca();
		this.ouvirMudancasMunPosto();
		this.ouvirMudancasTipoPCD();
		this.setValidators();
	}

	private getCasasDecimaisExportacao(): number {
		return ['PRECIPITACAO'].includes(this.variavelSelecionada) ? 1 : 2;
	}

	private ouvirMudancasPeriodoBusca(): void {
		this.inscricoes.add(
			this.inputs.form
				.get(FormularioRelatorio.PERIODO_BUSCA)
				?.valueChanges.subscribe(() => {
					this.atualizarRangeSelector();
					this.atualizarYAxis();
					this.atualizarDatasParaPeriodoMaisRecente();
					this.gerarRelatorio();
				})
		);
	}

	private atualizarYAxis() {
		const yAxis = [criarYAxisEsquerda()];

		this.chartOptions = {
			...this.chartOptions,
			yAxis,
		};
		if (this.chart) {
			this.chart.options = {
				...this.chartOptions,
				yAxis,
			};
		}
	}

	private atualizarRangeSelector(): void {
		const mapeamento: Record<string, () => Highcharts.RangeSelectorOptions> = {
			diario: () => rangeSelector5Horas,
			mensal: () => rangeSelector5Dias,
			anual: () => ({
				...rangeSelector5Meses,
				inputDateFormat: '%b %Y',
				inputEditDateFormat: '%m/%Y',
			}),
		};
		this.chartOptions = {
			...this.chartOptions,
			rangeSelector: mapeamento[this.periodoBusca](),
		};
	}

	private atualizarDatasParaPeriodoMaisRecente() {
		const hoje = new Date();
		this.dataInicio = this.getInicioJanelaMaisRecente(hoje);
		this.dataFim = hoje;
	}

	private get dataMinimaAtual(): Date | undefined {
		return this.inputs.datasPrimeirasColetasPCDs?.find(d => {
			return (
				d.id.toString() ===
				this.inputs.form.get(FormularioRelatorio.ESTACAO)?.value
			);
		})?.dataColeta;
	}

	private getInicioJanelaMaisRecente(dataFim: Date): Date {
		const mapeamento: Record<string, [number, moment.DurationInputArg2]> = {
			diario: [PERIODO_MAXIMO_EM_DIAS_GRAFICO_PCD, 'day'],
			mensal: [PERIODO_MAXIMO_EM_MESES_GRAFICO_PCD, 'month'],
			anual: [PERIODO_MAXIMO_EM_ANOS_GRAFICO_PCD, 'year'],
		};
		const [amount, unit] =
			mapeamento[this.periodoBusca] ?? mapeamento['diario'];
		const inicioJanela = this.getInicioDataByPeriodoBusca(
			moment(dataFim)
				.subtract(amount - 1, unit)
				.toDate()
		);
		const dataMinima = this.dataMinimaAtual;
		if (!dataMinima) {
			return inicioJanela;
		}
		const inicioMinimo = this.getInicioDataByPeriodoBusca(dataMinima);
		return inicioJanela < inicioMinimo ? inicioMinimo : inicioJanela;
	}

	private get periodoBusca(): string {
		return this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.value;
	}

	private getInicioDataByPeriodoBusca(data: Date): Date {
		const dataMoment = moment(data);
		const mapeamento: Record<string, moment.unitOfTime.StartOf> = {
			DIARIO: 'day',
			MENSAL: 'month',
			ANUAL: 'year',
		};
		const startOf = mapeamento[this.periodoBusca.toUpperCase()];
		return dataMoment.startOf(startOf).toDate();
	}

	private ouvirMudancasMensagemDataMin(): void {
		this.inscricoes.add(
			this.inputs.triggerTrocaMensagemDataMin$
				.pipe(filter(e => isNotNuloOuUndefined(e)))
				.subscribe(mensagem => {
					this.mensagemDataMinima = mensagem;
				})
		);
	}

	private ouvirMudancasMunPosto(): void {
		this.inscricoes.add(
			this.inputs.form
				.get(FormularioRelatorio.ESTACAO)
				?.valueChanges.subscribe(() => {
					this.atualizarDatasParaPeriodoMaisRecente();
				})
		);
	}

	private ouvirMudancasTipoPCD(): void {
		this.inscricoes.add(
			this.inputs.form
				.get(FormularioRelatorio.TIPO_PCD)
				?.valueChanges.subscribe(() => this.atualizarOpcoesFormulario())
		);
	}

	atualizarOpcoesFormulario(): void {
		if (this.variaveisArray.length === 0) {
			this.variaveisArray = enumAsSelectOptions(Variavel);
		}
		if (this.tiposPCD.length === 0) {
			this.tiposPCD = TIPOS_PCD_GRAFICO;
		}
		this.atualizarMunicipioPostosPorTipoPCD();
		this.atualizarVariaveisGraficoPorTipoPCD();
	}

	private atualizarVariaveisGraficoPorTipoPCD(): void {
		const tipoPCD = this.normalizarTipoPCD(
			this.inputs.form.get(FormularioRelatorio.TIPO_PCD)?.value
		);
		const variaveisDisponiveis =
			tipoPCD === 'METEOROLOGICA'
				? VARIAVEIS_GRAFICO_PCD_METEOROLOGICA
				: tipoPCD === 'AGROMETEOROLOGICA'
				? VARIAVEIS_GRAFICO_PCD_AGROMETEOROLOGICA
				: this.variaveisArray.map(variavel => variavel.value as string);

		this.variaveisGrafico = this.variaveisArray.filter(variavel =>
			variaveisDisponiveis.includes(variavel.value as string)
		);

		const variavelGraficoControl = this.inputs.form.get(
			FormularioRelatorio.VARIAVEL_GRAFICO
		);
		const variavelSelecionada = variavelGraficoControl?.value;
		const variavelContinuaDisponivel = this.variaveisGrafico.some(
			variavel => variavel.value === variavelSelecionada
		);

		if (!variavelContinuaDisponivel && this.variaveisGrafico.length > 0) {
			variavelGraficoControl?.setValue(this.variaveisGrafico[0].value, {
				emitEvent: false,
			});
		}
	}

	private atualizarMunicipioPostosPorTipoPCD(): void {
		const tipoPCDSelecionado = this.normalizarTipoPCD(
			this.inputs.form.get(FormularioRelatorio.TIPO_PCD)?.value
		);
		const estacoesFiltradas = this.estacoes.filter(estacao => {
			return (
				estacao.tipoEstacao === 'PCD' &&
				estacao.statusEstacao === 'ATIVA' &&
				this.normalizarTipoPCD(estacao.tipoPCD) === tipoPCDSelecionado
			);
		});

		this.opcoesMunPostos = estacoesFiltradas.map(estacao => ({
			name: corrigeDuplicacaoNome(
				`${estacao.nomeMunicipio}/${estacao.nomePosto}`
			),
			value: estacao.id.toString(),
		}));

		const estacaoControl = this.inputs.form.get(FormularioRelatorio.ESTACAO);
		const estacaoContinuaDisponivel = this.opcoesMunPostos.some(
			posto => posto.value === estacaoControl?.value
		);

		if (!estacaoContinuaDisponivel && this.opcoesMunPostos.length > 0) {
			estacaoControl?.setValue(this.opcoesMunPostos[0].value);
		}

		if (!estacaoContinuaDisponivel && this.opcoesMunPostos.length === 0) {
			estacaoControl?.setValue(null, { emitEvent: false });
		}
	}

	private normalizarTipoPCD(
		tipoPCD?: TipoPCD | keyof typeof TipoPCD | string | null
	): keyof typeof TipoPCD | undefined {
		if (!tipoPCD) {
			return undefined;
		}
		if (
			tipoPCD === 'AGROMETEOROLOGICA' ||
			tipoPCD === TipoPCD.AGROMETEOROLOGICA ||
			tipoPCD === 'Agrometeorológica'
		) {
			return 'AGROMETEOROLOGICA';
		}
		if (
			tipoPCD === 'METEOROLOGICA' ||
			tipoPCD === TipoPCD.METEOROLOGICA ||
			tipoPCD === 'Meteorológica'
		) {
			return 'METEOROLOGICA';
		}
		return undefined;
	}

	private get dataInicio(): Date {
		return this.inputs.form.get('dataInicio')?.value;
	}

	private get dataFim(): Date {
		return this.inputs.form.get('dataFim')?.value;
	}

	private set dataInicio(novaData: Date) {
		this.inputs.form.get('dataInicio')?.setValue(novaData);
	}

	private set dataFim(novaData: Date) {
		this.inputs.form.get('dataFim')?.setValue(novaData);
	}

	onChartCreated(chart: Highcharts.Chart) {
		this.chart = chart as Highcharts.Chart & HighchartsNoData;
		this.chart.hideNoData();
	}

	ngOnInit() {
		this.mensagemDataMinima = this.inputs.mensagemDataMinima;
		this.variaveisArray = enumAsSelectOptions(Variavel);
		this.variaveisGrafico = this.variaveisArray;
		this.tiposPCD = TIPOS_PCD_GRAFICO;
		this.variaveisUnidade = enumAsSelectOptions(VariavelUnidade);
		this.atualizarOpcoesFormulario();
		this.atualizarDatasParaPeriodoMaisRecente();
		this.gerarRelatorio();
	}

	ngAfterViewInit(): void {
		this.getTooltipOptions();
	}

	ngOnDestroy() {
		this.inscricoes.unsubscribe();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.VARIAVEL_GRAFICO)
			?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.TIPO_PCD)?.clearValidators();
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.ESTACAO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.VARIAVEL_GRAFICO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.TIPO_PCD)
			?.setValidators(Validators.required);
	}

	get nomeDaVariavel() {
		return this.variaveisArray.find(
			value => value.value === this.variavelSelecionada
		);
	}

	get unidadeDaVariavel() {
		return this.variaveisUnidade.find(
			value => value.value === this.variavelSelecionada
		)?.name;
	}

	private normalizarTexto(valor?: string | null): string {
		return (valor ?? '')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase();
	}

	private getChaveVariavelGrafico(valorSelecionado?: string | null): string {
		return (
			(this.variaveisArray.find(
				variavel =>
					variavel.value === valorSelecionado ||
					variavel.name === valorSelecionado
			)?.value as string | undefined) ??
			valorSelecionado ??
			''
		);
	}

	private isOpcaoDirecaoVento(valorSelecionado?: string | null): boolean {
		const variavel = this.variaveisArray.find(
			opcao =>
				opcao.value === valorSelecionado || opcao.name === valorSelecionado
		);
		return [valorSelecionado, variavel?.value, variavel?.name].some(valor =>
			this.normalizarTexto(valor).includes('direcao')
		);
	}

	private getConfiguracoesGrafico(): ConfiguracaoGrafico {
		const estacaoId = Number.parseInt(this.inputs.form.get('estacao')?.value);
		const estacao = this.estacoes.find(value => value.id === estacaoId);
		if (!estacao) {
			this.toastr.error(
				'Estação não encontrada.',
				'Erro ao obter informações do posto selecionado'
			);
			return new ConfiguracaoGrafico('', '', '', '', {});
		}
		const todosResultados = (
			this.chartOptions.rangeSelector ?? rangeSelector5Dias
		).buttons!.findIndex(botao => botao.type === 'all');
		return new ConfiguracaoGrafico('grafico', '', '', '', {
			rangeSelector: {
				selected: todosResultados,
			},
			title: {
				text: `${this.nomeDaVariavel?.name} ${this.unidadeDaVariavel} de ${estacao?.nomePosto}`,
				align: 'center',
			},
		});
	}

	getNoDataMessage(): any {
		if (this.inputs?.mensagemDataMinima) {
			return '<span>Esta estação não possui dados no período solicitado</span>';
		}
		return window.innerWidth < 768
			? 'Sem dados (estação climatológica).'
			: `<span> Esta estação não monitora essa variável ou não possui dados no período solicitado.</span>`;
	}

	getDadosTabelaParaExportacao(isPdf: boolean) {
		const tableData: any[] = [];
		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value as string;

		const colunas = [
			{ text: 'Município/Posto', fillColor: '#DCDCDC', alignment: 'center' },
			{ text: 'Data', fillColor: '#DCDCDC', alignment: 'center' },
		];

		if (this.isVariavelDirecaoVento) {
			colunas.push({
				text: `Direção ${VariavelUnidade.VENTO_DIRECAO}`,
				fillColor: '#DCDCDC',
				alignment: 'center',
			});
			colunas.push({
				text: 'Velocidade (m/s)',
				fillColor: '#DCDCDC',
				alignment: 'center',
			});
		} else {
			colunas.push({
				text: `${this.nomeDaVariavel?.name} ${this.unidadeDaVariavel}`,
				fillColor: '#DCDCDC',
				alignment: 'center',
			});
		}

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);
		if (!this.dados || !this.variavelSelecionada) {
			return tableData;
		}

		const dadosGrafico = mapearDados(
			this.variavelSelecionada,
			this.dados,
			periodo
		) as any;
		if (!dadosGrafico?.datas || !Array.isArray(dadosGrafico.datas)) {
			return tableData;
		}

		const municipios = this.dados.map(d => d.municipio).filter(Boolean);
		const postos = this.dados.map(d => d.nomePosto).filter(Boolean);
		const municipioPosto = corrigeDuplicacaoNome(
			`${municipios.length > 0 ? municipios[0] : ''}/${
				postos.length > 0 ? postos[0] : ''
			}`
		);
		this.dados.forEach((d, index) => {
			try {
				const dataGMT = new Date(dadosGrafico.datas[index]);
				const dataFormatada =
					periodo === 'mensal'
						? format(dataGMT, 'dd/MM/yyyy')
						: periodo === 'anual'
						? format(dataGMT, 'MM/yyyy')
						: format(dataGMT, 'dd/MM/yyyy HH:mm');
				const rowData = [
					municipioPosto || '-',
					alinharDado(dataFormatada, isPdf) || '-',
				];

				if (this.isVariavelDirecaoVento) {
					const direcao = dadosGrafico['direcaoVento']?.[index];
					const velocidade = dadosGrafico['velocidadeVento']?.[index];

					rowData.push(
						direcao != null
							? alinharDado(
									`${this.getWindDirectionLabel(
										direcao
									)} (${numberToBrNumberTables(direcao, 2)})`,
									isPdf
							  )
							: '-'
					);
					rowData.push(
						velocidade != null
							? alinharDado(numberToBrNumberTables(velocidade, 2), isPdf) || '-'
							: '-'
					);
				} else {
					const casasDecimais = this.getCasasDecimaisExportacao();
					const variavel = this.getCampoDadosGraficoParaExportacao();
					const valor = dadosGrafico[variavel]?.[index];
					rowData.push(
						valor != null
							? alinharDado(
									numberToBrNumberTables(valor, casasDecimais),
									isPdf
							  ) || '-'
							: '-'
					);
				}

				tableData.push(rowData);
			} catch (error) {
				console.error('Erro ao processar dados:', error);
			}
		});

		return tableData;
	}

	getDadosParaExportacaoXLSX() {
		const tableData: any[] = [];
		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value as string;
		const dadosGrafico = mapearDados(
			this.variavelSelecionada,
			this.dados,
			periodo
		) as any;
		if (!dadosGrafico?.datas || !Array.isArray(dadosGrafico.datas)) {
			return tableData;
		}

		const municipios = this.dados.map(d => d.municipio).filter(Boolean);
		const postos = this.dados.map(d => d.nomePosto).filter(Boolean);
		const municipioPosto = corrigeDuplicacaoNome(
			`${municipios.length > 0 ? municipios[0] : ''}/${
				postos.length > 0 ? postos[0] : ''
			}`
		);
		this.dados.forEach((d, index) => {
			try {
				const dataGMT = new Date(dadosGrafico.datas[index]);
				const dataFormatada =
					periodo === 'mensal'
						? format(dataGMT, 'dd/MM/yyyy')
						: periodo === 'anual'
						? format(dataGMT, 'MM/yyyy')
						: format(dataGMT, 'dd/MM/yyyy HH:mm');
				if (this.isVariavelDirecaoVento) {
					const direcao = dadosGrafico['direcaoVento']?.[index];
					const velocidade = dadosGrafico['velocidadeVento']?.[index];

					tableData.push({
						municipioPosto: municipioPosto || '-',
						data: dataFormatada || '-',
						valorDirecao:
							direcao != null
								? `${this.getWindDirectionLabel(direcao)} (${Number(
										direcao.toFixed(2)
								  )})`
								: '-',
						valorVelocidade:
							velocidade != null ? Number(velocidade.toFixed(2)) : '-',
					});
				} else {
					const casasDecimais = this.getCasasDecimaisExportacao();
					const variavel = this.getCampoDadosGraficoParaExportacao();
					tableData.push({
						municipioPosto: municipioPosto || '-',
						data: dataFormatada || '-',
						valor:
							dadosGrafico[variavel]?.[index] != null
								? Number(
										dadosGrafico[variavel]?.[index]?.toFixed(casasDecimais)
								  )
								: '-',
					});
				}
			} catch (error) {
				console.error('Erro ao processar dados:', error);
			}
		});
		return tableData;
	}

	private getCampoDadosGraficoParaExportacao(): string {
		const mapeamento: Record<string, string> = {
			EVAPOTRANSPIRACAO: 'precipitacao',
			VENTO_VELOCIDADE_1: 'velocidadeVento',
			VENTO_VELOCIDADE_2: 'velocidadeVento',
			VENTO_1_MAXIMA: 'velocidadeVento',
			VENTO_2_MAXIMA: 'velocidadeVento',
			VENTO_VELOCIDADE_1_MAXIMA: 'velocidadeVento',
			VENTO_VELOCIDADE_2_MAXIMA: 'velocidadeVento',
		};
		return (
			mapeamento[this.variavelSelecionada] ??
			this.variavelSelecionada.split('_')[0].toLowerCase()
		);
	}

	async exportarPDF() {
		const chartsConverted: TDocumentDefinitions['content'] = [];

		chartsConverted.push({
			image: await criarImagemBase64FromChart(this.chart),
			fontSize: 10,
			alignment: 'center',
			height: 400,
			width: 750,
		});

		const documentDefinition: any = await pdfseira.documentDefinitions(
			'landscape'
		);
		documentDefinition.pageOrientation = 'landscape';

		const content = [
			{
				text: this.tituloGrafico,
				alignment: 'center',
				margin: [15, 15, 5, 15],
			},
			...chartsConverted,
		];

		documentDefinition.content.push(content);

		const tableData = this.getDadosTabelaParaExportacao(true);

		documentDefinition.content.push(
			{
				text: '',
				alignment: 'center',
				fontSize: 10,
			},
			{
				table: {
					widths: this.isVariavelDirecaoVento
						? ['25%', '25%', '25%', '25%']
						: ['33%', '33%', '33%'],
					body: tableData,
					fontSize: 12,
				},
				marginTop: 100,
			}
		);
		return baixarEAbrirPDF(this.tituloGrafico, documentDefinition);
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(false) ?? [];
		DocumentExporter.gerarCSV(tableData, this.tituloGrafico);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(false);
		let txtData = '';

		if (Array.isArray(tableData) && tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(txtData, this.tituloGrafico);
	}

	exportarXLSX() {
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const headers = [
			{ label: 'municipioPosto', text: 'Município/Posto', size: 24 },
			{ label: 'data', text: 'Data', size: 24 },
		];

		if (this.isVariavelDirecaoVento) {
			headers.push({
				label: 'valorDirecao',
				text: `Direção ${VariavelUnidade.VENTO_DIRECAO}`,
				size: 24,
			});
			headers.push({
				label: 'valorVelocidade',
				text: 'Velocidade (m/s)',
				size: 24,
			});
		} else {
			headers.push({
				label: 'valor',
				text: `${this.nomeDaVariavel?.name} ${this.unidadeDaVariavel}`,
				size: 24,
			});
		}

		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number,
						fromHeader: boolean,
						styleKeys: string[]
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else if (colIndex > 0) {
							return 'rowStyleCenter';
						} else {
							return 'rowStyle';
						}
					},
					headers: headers,
					data: this.getDadosParaExportacaoXLSX(),
					columns: [],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: this.tituloGrafico,
						styleId: 'title',
					},
				},
			],
			fileName: this.tituloGrafico,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	gerarRelatorio() {
		const variavelSelecionadaForm =
			this.inputs.form.get('variavelGrafico')?.value;
		this.variavelSelecionada = this.getChaveVariavelGrafico(
			variavelSelecionadaForm
		);
		const estacao = Number.parseInt(this.inputs.form.get('estacao')?.value);
		const periodoBusca = this.inputs.form.get('periodoBusca')?.value;
		if (!this.variavelSelecionada || Number.isNaN(estacao)) {
			return;
		}
		this.isVariavelVento = this.variavelSelecionada.split('_')[0] === 'VENTO';
		this.isVariavelDirecaoVento =
			VARIAVEIS_DIRECAO_VENTO.includes(this.variavelSelecionada) ||
			this.isOpcaoDirecaoVento(variavelSelecionadaForm);

		this.loading = true;
		this.inputs.setLoading(true);

		this.tituloGrafico = `Variáveis meteorológicas - ${this.nomeDaVariavel
			?.name} ${this.unidadeDaVariavel} - ${DateTimeUtils.formatarDataPeriodo(
			this.dataInicio,
			this.dataFim,
			periodoBusca
		)}`;

		const variavelRequisicao =
			this.variavelSelecionada === 'PRECIPITACAO'
				? ('PRECIPITACAO_DO_PERIODO' as VariavelAcompanhamentoColeta)
				: (this.variavelSelecionada as VariavelAcompanhamentoColeta);
		const requisicao = this.isVariavelDirecaoVento
			? this.variavelService.getMedicoesDeVentoPorEstacao(
					variavelRequisicao,
					this.dataInicio,
					this.dataFim,
					estacao,
					periodoBusca
			  )
			: this.variavelService.getMedicoesdeVariavelPorEstacao(
					variavelRequisicao,
					this.dataInicio,
					this.dataFim,
					estacao,
					periodoBusca
			  );

		requisicao.subscribe({
			next: (
				resp:
					| RelatorioVariavelEstacao[]
					| RelatorioVariavelEstacaoVento[]
					| RelatorioDirecaoVentoEstacao[]
			) => {
				this.dados = resp;
				const dadosGrafico = mapearDados(
					this.variavelSelecionada,
					this.dados,
					this.inputs.form.get('periodoBusca')?.value
				) as any;
				this.isVariavelDirecaoVento =
					VARIAVEIS_DIRECAO_VENTO.includes(this.variavelSelecionada) ||
					this.isOpcaoDirecaoVento(variavelSelecionadaForm);
				Array.isArray(dadosGrafico.datas) && dadosGrafico.datas.length === 0
					? this.relatoriosService.setExibirBotoesExportacao(false)
					: this.relatoriosService.setExibirBotoesExportacao(true);
				this.atualizarRangeSelector();
				this.atualizarYAxis();
			},
			error: (erro: any) => {
				this.loading = false;
				this.inputs.setLoading(false);
				const msg_erro = obter_erro_request(erro);
				this.toastr.error(
					msg_erro,
					'Erro ao obter informações do posto selecionado'
				);
			},
			complete: () => {
				this.loading = false;
				this.inputs.setLoading(false);
				this.montarGrafico();
			},
		});
	}

	reiniciarGrafico() {
		const periodo = this.inputs.form.get('periodoBusca')?.value;
		const variavel = this.variavelSelecionada.split('_')[0];
		const todosZero =
			this.dados.length > 0
				? (this.dados as any).every((d: any) => d?.valor === 0)
				: false;
		this.chartOptions.lang = {
			noData: this.getNoDataMessage(),
		};
		if (this.dados.length === 0) {
			this.chartOptions.chart = {
				...this.chartOptions.chart,
				zooming: {
					mouseWheel: false,
				},
			};
		}

		if (variavel.includes('PRECIPITACAO') && !todosZero) {
			this.chartOptions.navigator = {
				enabled: true,
				series: {
					type: 'column',
					color: '#7cb5ec',
				},
			};
		} else if (todosZero) {
			this.chartOptions.navigator = {
				enabled: true,
				series: { type: 'line', color: '#7cb5ec', data: [] },
			};
		} else {
			this.chartOptions.navigator = {
				enabled: true,
			};
		}

		// Ajusta minRange dinamicamente por período
		const minRangeConfig: Record<string, number> = {
			diario: 2,
			mensal: this.UM_DIA_MILISEGUNDOS, // 1 dia em ms
			anual: this.MES_MILISEGUNDOS, // 1 mês em ms
		};
		const deveExibirTickInicial = deveAplicarTickInicialEixoX(periodo);

		this.chartOptions.xAxis = {
			type: 'datetime',
			zoomEnabled: true,
			ordinal: false,
			startOnTick: true,
			minRange: minRangeConfig[periodo] || 2,
			showFirstLabel: true,
			...(deveExibirTickInicial
				? {
						tickPositioner: function (this: Highcharts.Axis) {
							return obterTicksComInicioEixoX(
								this.tickPositions ?? [],
								this.min,
								this.max
							);
						},
				  }
				: {}),
			labels: {
				...opcoesLabelInicioEixoX,
				formatter: function () {
					if (
						periodo === 'diario' &&
						Math.abs(Number(this.value) - Number(this.axis.min)) <=
							TOLERANCIA_DUPLICIDADE_TICK_EIXO_X
					) {
						return Highcharts.dateFormat('%H:%M', Number(this.value));
					}
					if (periodo === 'mensal') {
						return Highcharts.dateFormat('%d/%m/%Y', Number(this.value));
					}

					if (periodo === 'anual') {
						const data = new Date(Number(this.value));

						data.setUTCMonth(data.getUTCMonth() + 1);

						return Highcharts.dateFormat('%m/%Y', data.getTime());
					}

					return Highcharts.dateFormat('%d/%m/%Y', Number(this.value));
				},
				step: 1,
			},
			events: {
				setExtremes: e => {
					const chart = (e as any).target.chart;
					const MES_MILISEGUNDOS = this.MES_MILISEGUNDOS;
					const SEMESTRE_MILISEGUNDOS = this.SEMESTRE_MILISEGUNDOS;

					if (e.min && e.max) {
						const range = e.max - e.min; // intervalo em milissegundos
						if (range < MES_MILISEGUNDOS) {
							chart.xAxis[0].update({
								labels: {
									...opcoesLabelInicioEixoX,
									rotation: -45,
									format: '{value:%d/%m}',
								},
							});
						} else if (range > SEMESTRE_MILISEGUNDOS) {
							chart.xAxis[0].update({
								labels: {
									...opcoesLabelInicioEixoX,
									rotation: 0,
									format: '{value:%b/%Y}',
								},
							});
						} else {
							chart.xAxis[0].update({
								labels: {
									...opcoesLabelInicioEixoX,
									rotation: -25,
									format: '{value:%d/%m/%Y}',
								},
							});
						}
					}
				},
				afterSetExtremes: (e: any) => {
					if (e.trigger === 'rangeSelectorButton') {
						const range = e.max - e.min;
						const newMin = e.dataMin ?? e.min;
						const newMax = newMin + range;

						if (e.min !== newMin) {
							const axis = e.target as unknown as Highcharts.Axis;
							axis.chart.xAxis[0].setExtremes(newMin, newMax, true, true);
						}
					}
				},
			},
		};
		setTimeout(() => {
			if (!this.highStockComponent) {
				return;
			}
			this.highStockComponent.reiniciarGrafico(
				this.chartOptions,
				undefined,
				500
			);
		});
	}

	montarGrafico() {
		if (this.chart && this.chartOptions.lang?.noData) {
			this.chart.showNoData(this.chartOptions.lang.noData);
		}

		const dadosGrafico = obterDadosSeriePorResultado(
			this.variavelSelecionada,
			0,
			this.dados,
			this.inputs.form.get('periodoBusca')?.value
		);

		const series = Array.isArray(dadosGrafico?.serie)
			? (dadosGrafico.serie as SeriesOptionsType[])
			: [];

		if (this.chart) {
			this.chart.xAxis[0].setExtremes(undefined, undefined, false);
			this.chart.update(this.chartOptions, true, true);
		}

		if (dadosGrafico) {
			const yAxis = Array.isArray(this.chartOptions.yAxis)
				? [...this.chartOptions.yAxis]
				: [criarYAxisEsquerda()];
			yAxis[0] = {
				...criarYAxisEsquerda(),
				...dadosGrafico.eixo,
				labels: {
					...criarYAxisEsquerda().labels,
					...(dadosGrafico.eixo as Highcharts.YAxisOptions).labels,
					formatter: function () {
						return formatarLabelEixoY(this.value);
					},
				},
				...(this.isVariavelDirecaoVento ? { lineWidth: 0 } : {}),
				minPadding: 0.05,
				maxPadding: 0.08,
				startOnTick: false,
				endOnTick: false,
			};
			delete yAxis[0].min;
			delete yAxis[0].max;
			delete yAxis[0].softMin;
			delete yAxis[0].softMax;
			delete yAxis[0].tickPositions;
			this.chartOptions = {
				...this.chartOptions,
				yAxis,
			};

			const config = this.getConfiguracoesGrafico();
			this.chartOptions = deepmerge(
				this.chartOptions,
				config.configuracoesExtras
			);
		}

		this.chartOptions = {
			...this.chartOptions,
			series,
		};

		this.reiniciarGrafico();

		if (this.chart) {
			this.chart.update(this.chartOptions);
			return;
		}
	}

	getTooltipOptions() {
		const getBeaufortDescricao = (velocidadeVento: number): string => {
			const beaufortScale = [
				{ limite: 1, descricao: 'Calmo' },
				{ limite: 4, descricao: 'Aragem' },
				{ limite: 7, descricao: 'Brisa leve' },
				{ limite: 11, descricao: 'Brisa fraca' },
				{ limite: 17, descricao: 'Brisa moderada' },
				{ limite: 22, descricao: 'Brisa forte' },
				{ limite: 28, descricao: 'Vento fresco' },
				{ limite: 34, descricao: 'Vento forte' },
				{ limite: 41, descricao: 'Ventania' },
				{ limite: 48, descricao: 'Ventania forte' },
				{ limite: 56, descricao: 'Tempestade' },
				{ limite: 64, descricao: 'Tempestade violenta' },
			];

			for (const item of beaufortScale) {
				if (velocidadeVento <= item.limite) {
					return item.descricao;
				}
			}

			return 'Furacão';
		};

		const classContext = this;

		this.chartOptions.tooltip = {
			padding: 0,
			borderRadius: 5,
			useHTML: true,
			shared: true,
			split: false,
			formatter: function () {
				const periodo = classContext.inputs.form.get('periodoBusca')?.value;
				const formatoData =
					periodo === 'diario'
						? "EEEE, d 'de' MMMM, HH:mm"
						: periodo === 'mensal'
						? "dd 'de' MMMM 'de' yyyy"
						: "MMMM 'de' yyyy";
				const title = classContext.formatarHorario(Number(this.x), formatoData);
				const pontos = (
					(this.points ?? [this]) as Highcharts.TooltipFormatterContextObject[]
				).filter(point => point?.series?.name);
				const pontoVelocidade = pontos.find(point =>
					classContext.normalizarTexto(point.series.name).includes('velocidade')
				);
				const pontoDirecao =
					pontos.find(point =>
						classContext.normalizarTexto(point.series.name).includes('direcao')
					) ??
					(pontos[0]?.series.chart.series
						.find(serie =>
							classContext.normalizarTexto(serie.name).includes('direcao')
						)
						?.points.find(point => point.x === this.x) as
						| Highcharts.TooltipFormatterContextObject
						| undefined);

				if (
					classContext.isVariavelDirecaoVento &&
					(pontoVelocidade || pontoDirecao)
				) {
					const velocidade =
						typeof pontoVelocidade?.y === 'number'
							? pontoVelocidade.y
							: typeof pontoDirecao?.y === 'number'
							? pontoDirecao.y
							: typeof (pontoDirecao as any)?.value === 'number'
							? (pontoDirecao as any).value
							: undefined;
					const pontoDirecaoHighcharts =
						(pontoDirecao?.point as any) ?? (pontoDirecao as any);
					const direcao =
						pontoDirecaoHighcharts?.direction ??
						pontoDirecaoHighcharts?.options?.direction;
					const beaufortLevel =
						pontoDirecaoHighcharts?.beaufortLevel ??
						pontoDirecaoHighcharts?.options?.beaufortLevel;
					const corVelocidade = pontoVelocidade?.color ?? '#2daae1';
					const corDirecao = pontoDirecao?.color ?? '#8f83d8';

					return `
						<div style="font-size:12px; min-width:160px">
							<div class="d-flex align-items-center px-2 py-1" style="height:30px">
								${capitalizeFirstLetter(title)}
							</div>
							<div class="d-flex align-items-center fw-semibold px-2 py-1"
								style="background-color:${corVelocidade};height:30px">
								Velocidade:
								<span class="ps-1 me-1 fw-normal" style="font-size:16px">
									${velocidade != null ? numberToBrNumberTables(velocidade, 2) + ' m/s' : '-'}
								</span>
							</div>
							<div class="d-flex align-items-center fw-semibold px-2 py-1"
								style="background-color:${corVelocidade};height:30px">
								${velocidade != null ? getBeaufortDescricao(velocidade) : '-'}
							</div>
							<div class="d-flex align-items-center fw-semibold px-2 py-1"
								style="background-color:${corDirecao}75;height:30px">
								Direção:
								<span class="ps-1 me-1 fw-normal" style="font-size:16px">
									${direcao != null ? classContext.getWindDirectionLabel(direcao) : '-'}
								</span>
							</div>
							${
								beaufortLevel != null
									? `
							<div class="d-flex align-items-center fw-semibold px-2 py-1"
								style="background-color:#A4C1E575;height:30px">
								${classContext.translateBeaufort(beaufortLevel)}
							</div>`
									: ''
							}
						</div>`;
				}

				const makeTooltip = (
					points: Highcharts.TooltipFormatterContextObject[]
				) => {
					// Obtém o período selecionado do formulário
					const periodo = classContext.inputs.form.get('periodoBusca')?.value;
					let formatoData: string;

					if (periodo === 'diario') {
						formatoData = "EEEE, d 'de' MMMM, HH:mm";
					} else if (periodo === 'mensal') {
						formatoData = "dd 'de' MMMM 'de' yyyy";
					} else {
						formatoData = "MMMM 'de' yyyy";
					}
					const title = classContext.formatarHorario(
						Number(this.x),
						formatoData
					);

					let tooltipContent = `
						<div style="font-size: 12px">
							<div class="d-flex align-items-center px-2 py-1" style="height:30px">
								${capitalizeFirstLetter(title)}
							</div>`;

					points
						.sort((a, b) => {
							const aNome = classContext.normalizarTexto(a.series.name);
							const bNome = classContext.normalizarTexto(b.series.name);
							if (aNome.includes('velocidade')) {
								return -1;
							}
							if (bNome.includes('velocidade')) {
								return 1;
							}
							return 0;
						})
						.forEach(point => {
							const casasDecimais =
								classContext.variavelSelecionada === 'PRECIPITACAO' ? 1 : 2;
							const direction = (point.point as any).direction;
							const nomeSerie = classContext.normalizarTexto(point.series.name);
							const isVelocidade = nomeSerie.includes('velocidade');
							const descricaoVento = isVelocidade
								? getBeaufortDescricao(point.y as number)
								: '';
							const beaufortLevel = (point.point as any).beaufortLevel;
							const unidade =
								isVelocidade && classContext.isVariavelDirecaoVento
									? 'm/s'
									: classContext.unidadeDaVariavel
											?.replaceAll('(', '')
											.replaceAll(')', '') ?? '';

							tooltipContent += `
							<div class="d-flex align-items-center fw-semibold px-2 py-1"
								style="background-color:${point.color}75;height:30px">
								${point.series.name}:
								<span class="ps-1 me-1 fw-normal" style="font-size:16px">
									${
										direction != null
											? classContext.getWindDirectionLabel(direction)
											: numberToBrNumberTables(point.y, casasDecimais) +
											  (unidade ? ' ' + unidade : '')
									}
								</span>
							</div>`;

							if (descricaoVento) {
								tooltipContent += `
								<div class="d-flex align-items-center fw-semibold px-2 py-1"
									style="background-color:${point.color}75;height:30px">
									${descricaoVento}
								</div>`;
							}

							if (beaufortLevel != null) {
								tooltipContent += `
								<div class="d-flex align-items-center fw-semibold px-2 py-1"
									style="background-color:#A4C1E575;height:30px">
									${classContext.translateBeaufort(beaufortLevel)}
								</div>`;
							}
						});

					tooltipContent += `</div>`;
					return tooltipContent;
				};

				const tooltips: string[] = [];
				const points: Record<
					string,
					Highcharts.TooltipFormatterContextObject[]
				> = {};

				this.points?.forEach(point => {
					if (['Baixa', 'Média', 'Alta'].includes(point.series.name)) {
						if (points['Nebulosidade']) {
							points['Nebulosidade'].push(point);
						} else {
							points['Nebulosidade'] = [point];
						}
					} else if (['Direção', 'Velocidade'].includes(point.series.name)) {
						if (points['Vento']) {
							points['Vento'].push(point);
						} else {
							points['Vento'] = [point];
						}
					} else {
						points[point.series.name] = [point];
					}
				});

				Object.keys(points).forEach(nomeGrupo => {
					const nomeNormalizado = classContext.normalizarTexto(nomeGrupo);
					if (
						nomeGrupo !== 'Vento' &&
						(nomeNormalizado.includes('direcao') ||
							nomeNormalizado.includes('velocidade'))
					) {
						points['Vento'] = [
							...(points['Vento'] ?? []),
							...points[nomeGrupo],
						];
						delete points[nomeGrupo];
					}
				});

				Object.values(points).forEach(points => {
					tooltips.push(makeTooltip(points));
					if (points.length > 1) {
						for (let i = 0; i < points.length - 1; i++) {
							tooltips.push('');
						}
					}
				});

				return tooltips.join('');
			},
		};
	}
	getWindDirectionLabel(direction: number) {
		if (direction < 0) {
			direction = 360 + (direction % 360);
		}

		const directions = [
			'Norte',
			'Norte-nordeste',
			'Nordeste',
			'Leste-nordeste',
			'Leste',
			'Leste-sudeste',
			'Sudeste',
			'Sul-sudeste',
			'Sul',
			'Sul-sudoeste',
			'Sudoeste',
			'Oeste-sudoeste',
			'Oeste',
			'Oeste-noroeste',
			'Noroeste',
			'Norte-noroeste',
		];

		const index = Math.round(direction / 22.5) % 16;
		return directions[index];
	}

	translateBeaufort(beaufortValue: number) {
		const translations = [
			'Calmo',
			'Pouco vento fraco',
			'Vento fraco',
			'Vento moderado',
			'Vento moderado a forte',
			'Vento forte',
			'Vento muito forte',
			'Vento duro',
			'Vendaval',
			'Vendaval forte',
			'Vendaval muito forte',
			'Vendaval severo',
			'Vendaval extremo',
		];
		if (beaufortValue >= 0 && beaufortValue <= 12) {
			return translations[beaufortValue];
		}

		return 'Desconhecido';
	}

	formatarHorario(horario: number | string | Date, formato: string) {
		const date =
			typeof horario === 'number' ? new Date(horario) : new Date(horario);
		return format(date, formato, {
			locale: ptBrLocale,
		});
	}
}
